import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQrcode } from '@fortawesome/pro-light-svg-icons/faQrcode';
import { TypedMemo } from '../../common/types';
import { SMLCopyLinkButton } from './SMLCopyLinkButton';
import { SMLPublishedQRBox } from '../published/SMLPublishedQRBox';
import { splitShareUrl } from '../published/publishedPageFacts';
import { Panel } from '../../music24/components/primitives/Panel';
import styles from './SMLPageUrlBar.module.scss';

interface SMLPageUrlBarProps {
  shareUrl: string | null;
  /** Shown when the page has no address yet, so the bar still says what it will be. */
  slug: string;
  isPublished: boolean;
}

/**
 * The address, and the two things done with it.
 *
 * Copy is the errand the artist came for, so it is the filled control. The QR
 * code is the same address as a picture, and it opens under the bar rather
 * than in a window of its own. The embed snippet moved to the ⋯ menu: it is
 * asked for once a year and was standing next to the button used daily.
 */
const SMLPageUrlBarComponent: React.FC<SMLPageUrlBarProps> = ({ shareUrl, slug, isPublished }) => {
  const intl = useIntl();
  const [isQROpen, setIsQROpen] = useState(false);
  const toggleQR = useCallback(() => setIsQROpen((wasOpen) => !wasOpen), []);

  const address = useMemo(() => (shareUrl !== null ? splitShareUrl(shareUrl) : { host: '', slug }), [shareUrl, slug]);

  return (
    <div className={styles.wrapper}>
      <Panel className={styles.bar}>
        <span className={clsx(styles.pill, isPublished && styles.pillLive)}>
          {isPublished && <i className={styles.dot} />}
          {isPublished
            ? intl.formatMessage({ id: 'app.sml.smartlinks.status.live', defaultMessage: 'Live' })
            : intl.formatMessage({ id: 'app.sml.smartlinks.status.draft', defaultMessage: 'Draft' })}
        </span>
        <span className={styles.address} title={shareUrl ?? slug}>
          <span className={styles.host}>{address.host}</span>
          <span className={styles.slug}>{address.slug}</span>
        </span>
        {shareUrl !== null && (
          <span className={styles.actions}>
            <SMLCopyLinkButton shareUrl={shareUrl} />
            <button
              type="button"
              className={clsx(styles.qr, isQROpen && styles.qrOpen)}
              onClick={toggleQR}
              aria-expanded={isQROpen}
            >
              <FontAwesomeIcon icon={faQrcode} />
              {intl.formatMessage({ id: 'app.sml.details.qr', defaultMessage: 'QR' })}
            </button>
          </span>
        )}
      </Panel>
      {shareUrl !== null && isQROpen && <SMLPublishedQRBox shareUrl={shareUrl} slug={splitShareUrl(shareUrl).slug} />}
    </div>
  );
};

export const SMLPageUrlBar = TypedMemo(SMLPageUrlBarComponent);
